import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, Menu, X, ChevronRight, Minus, Plus, Trash2, Lock, LogOut, PlusCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_1818d098-2133-4078-9531-acababa9e2e1/artifacts/6ej2l0dq_fresh%20poboy%20logo%20b.webp";

// Auth Context
const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem("adminAuth");
    if (saved) {
      const parsed = JSON.parse(saved);
      setCredentials(parsed);
      setIsAdmin(true);
    }
  }, []);

  const login = (username, password) => {
    const creds = { username, password };
    localStorage.setItem("adminAuth", JSON.stringify(creds));
    setCredentials(creds);
    setIsAdmin(true);
  };

  const logout = () => {
    localStorage.removeItem("adminAuth");
    setCredentials(null);
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ isAdmin, credentials, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return useContext(AuthContext);
}

// Navbar
function Navbar({ cartCount, onMenuClick, menuOpen }) {
  const { isAdmin, logout } = useAuth();
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-red-600/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="logo">
            <img src={BRAND_LOGO} alt="Fresh Po'Boy" className="h-14 w-auto" />
          </Link>
          
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Home</Link>
            <Link to="/shop" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Shop</Link>
            <Link to="/our-story" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Our Story</Link>
            <Link to="/lifestyle" className="text-white/80 hover:text-cyan-400 transition-colors text-sm tracking-wide">Lifestyle</Link>
            <Link to="/hiphop" className="text-red-500 hover:text-red-400 transition-colors text-sm tracking-wide font-semibold">Hip Hop</Link>
            <Link to="/faith" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm tracking-wide font-semibold">Faith</Link>
            <Link to="/sports" className="text-red-500 hover:text-red-400 transition-colors text-sm tracking-wide font-semibold">Sports</Link>
            <Link to="/streetwear" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm tracking-wide font-semibold">Streetwear</Link>
            <Link to="/hoodies" className="text-red-500 hover:text-red-400 transition-colors text-sm tracking-wide font-semibold">Hoodies</Link>
            <Link to="/anime" className="text-cyan-400 hover:text-cyan-300 transition-colors text-sm tracking-wide font-semibold">Anime</Link>
            {isAdmin && (
              <Link to="/admin/add-product" className="text-green-400 hover:text-green-300 transition-colors text-sm tracking-wide font-semibold flex items-center gap-1">
                <PlusCircle size={16} /> Add Product
              </Link>
            )}
          </div>

          <div className="flex items-center gap-4">
            {isAdmin ? (
              <button onClick={logout} className="p-2 text-green-400 hover:text-green-300" title="Logout">
                <LogOut size={20} />
              </button>
            ) : (
              <Link to="/admin/login" className="p-2 text-white/60 hover:text-white" title="Admin">
                <Lock size={20} />
              </Link>
            )}
            <Link to="/cart" className="relative p-2 text-white/80 hover:text-white">
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-amber-500 text-black text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <button onClick={onMenuClick} className="md:hidden p-2 text-white">
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-black border-t border-white/10 px-4 py-4 space-y-3">
          <Link to="/" className="block text-white/80 py-2" onClick={onMenuClick}>Home</Link>
          <Link to="/shop" className="block text-white/80 py-2" onClick={onMenuClick}>Shop</Link>
          <Link to="/our-story" className="block text-white/80 py-2" onClick={onMenuClick}>Our Story</Link>
          <Link to="/lifestyle" className="block text-white/80 py-2" onClick={onMenuClick}>Lifestyle</Link>
          <Link to="/hiphop" className="block text-red-400 py-2 font-semibold" onClick={onMenuClick}>Hip Hop</Link>
          <Link to="/faith" className="block text-yellow-400 py-2 font-semibold" onClick={onMenuClick}>Faith</Link>
          <Link to="/streetwear" className="block text-purple-400 py-2 font-semibold" onClick={onMenuClick}>Streetwear</Link>
          <Link to="/hoodies" className="block text-cyan-400 py-2 font-semibold" onClick={onMenuClick}>Hoodies</Link>
          <Link to="/anime" className="block text-amber-400 py-2 font-semibold" onClick={onMenuClick}>Anime</Link>
          <Link to="/sports" className="block text-orange-400 py-2 font-semibold" onClick={onMenuClick}>Sports</Link>
          {isAdmin && <Link to="/admin/add-product" className="block text-green-400 py-2" onClick={onMenuClick}>Add Product</Link>}
        </div>
      )}
    </nav>
  );
}

// Admin Login
function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      if (res.data.success) {
        login(username, password);
        navigate("/admin/add-product");
      }
    } catch {
      setError("Invalid credentials");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-md bg-zinc-900 rounded-lg p-8 border border-white/10">
        <div className="text-center mb-8">
          <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-white text-3xl font-bebas">Admin Login</h1>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded">{error}</div>}
          <div>
            <label className="text-white text-sm font-semibold block mb-2">Username</label>
            <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none" required />
          </div>
          <div>
            <label className="text-white text-sm font-semibold block mb-2">Password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none" required />
          </div>
          <button type="submit" disabled={loading} className="w-full py-4 bg-amber-500 text-black font-bold hover:bg-amber-400 disabled:opacity-50">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Add Product Page
function AddProductPage({ onProductAdded }) {
  const navigate = useNavigate();
  const { isAdmin, credentials } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [colorInput, setColorInput] = useState("");
  
  const [form, setForm] = useState({
    name: "", price: "", category: "T-Shirts", collection: "Anime",
    image: "", video: "", description: "", sizes: ["S", "M", "L", "XL", "2XL"], colors: ["Black"]
  });

  useEffect(() => {
    if (!isAdmin) navigate("/admin/login");
  }, [isAdmin, navigate]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const toggleSize = (size) => {
    setForm(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size) ? prev.sizes.filter(s => s !== size) : [...prev.sizes, size]
    }));
  };

  const addColor = () => {
    if (colorInput.trim() && !form.colors.includes(colorInput.trim())) {
      setForm(prev => ({ ...prev, colors: [...prev.colors, colorInput.trim()] }));
      setColorInput("");
    }
  };

  const removeColor = (color) => setForm(prev => ({ ...prev, colors: prev.colors.filter(c => c !== color) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(false);
    try {
      const productData = { ...form, price: parseFloat(form.price) };
      if (!productData.video) delete productData.video;
      await axios.post(`${API}/products`, productData, {
        auth: { username: credentials.username, password: credentials.password }
      });
      setSuccess(true);
      setForm({ name: "", price: "", category: "T-Shirts", collection: "Anime", image: "", video: "", description: "", sizes: ["S", "M", "L", "XL", "2XL"], colors: ["Black"] });
      if (onProductAdded) onProductAdded();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to add product");
    }
    setLoading(false);
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-black pt-24 pb-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <PlusCircle className="w-8 h-8 text-green-400" />
          <h1 className="text-white text-4xl font-bebas">Add New Product</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 bg-zinc-900 rounded-lg p-8 border border-white/10">
          {success && <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded">Product added!</div>}
          {error && <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded">{error}</div>}

          <div>
            <label className="text-white text-sm font-semibold block mb-2">Product Name *</label>
            <input type="text" name="name" value={form.name} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none" required />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white text-sm font-semibold block mb-2">Price ($) *</label>
              <input type="number" name="price" value={form.price} onChange={handleChange} step="0.01" min="0" className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none" required />
            </div>
            <div>
              <label className="text-white text-sm font-semibold block mb-2">Category *</label>
              <select name="category" value={form.category} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none">
                <option value="T-Shirts">T-Shirts</option>
                <option value="Hoodies">Hoodies</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-white text-sm font-semibold block mb-2">Collection *</label>
            <select name="collection" value={form.collection} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none">
              <option value="Anime">Anime</option>
              <option value="Hip Hop">Hip Hop</option>
              <option value="Faith">Faith</option>
              <option value="Sports">Sports</option>
              <option value="Streetwear">Streetwear</option>
              <option value="Hoodies">Hoodies</option>
              <option value="Featured">Featured</option>
            </select>
          </div>

          <div>
            <label className="text-white text-sm font-semibold block mb-2">Image URL *</label>
            <input type="url" name="image" value={form.image} onChange={handleChange} className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none" required />
            {form.image && <img src={form.image} alt="Preview" className="mt-3 w-32 h-32 object-cover rounded border border-white/10" />}
          </div>

          <div>
            <label className="text-white text-sm font-semibold block mb-2">Video URL (Optional)</label>
            <input type="url" name="video" value={form.video} onChange={handleChange} placeholder="Add a product video URL (.mp4)" className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none placeholder:text-white/30" />
            {form.video && (
              <video src={form.video} className="mt-3 w-48 h-32 object-cover rounded border border-white/10" muted />
            )}
          </div>

          <div>
            <label className="text-white text-sm font-semibold block mb-2">Description *</label>
            <textarea name="description" value={form.description} onChange={handleChange} rows={3} className="w-full px-4 py-3 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none resize-none" required />
          </div>

          <div>
            <label className="text-white text-sm font-semibold block mb-3">Sizes *</label>
            <div className="flex flex-wrap gap-3">
              <button type="button" onClick={() => toggleSize("S")} className={`px-4 py-2 border ${form.sizes.includes("S") ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>S</button>
              <button type="button" onClick={() => toggleSize("M")} className={`px-4 py-2 border ${form.sizes.includes("M") ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>M</button>
              <button type="button" onClick={() => toggleSize("L")} className={`px-4 py-2 border ${form.sizes.includes("L") ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>L</button>
              <button type="button" onClick={() => toggleSize("XL")} className={`px-4 py-2 border ${form.sizes.includes("XL") ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>XL</button>
              <button type="button" onClick={() => toggleSize("2XL")} className={`px-4 py-2 border ${form.sizes.includes("2XL") ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>2XL</button>
              <button type="button" onClick={() => toggleSize("3XL")} className={`px-4 py-2 border ${form.sizes.includes("3XL") ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>3XL</button>
            </div>
          </div>

          <div>
            <label className="text-white text-sm font-semibold block mb-3">Colors *</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {form.colors.length > 0 && form.colors.map((c, i) => (
                <span key={i} className="px-3 py-1 bg-zinc-800 text-white rounded-full text-sm flex items-center gap-2">
                  {c}
                  <button type="button" onClick={() => removeColor(c)} className="text-white/60 hover:text-red-400"><X size={14} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={colorInput} onChange={(e) => setColorInput(e.target.value)} onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addColor())} className="flex-1 px-4 py-2 bg-black border border-white/20 rounded text-white focus:border-amber-400 focus:outline-none" placeholder="Add color" />
              <button type="button" onClick={addColor} className="px-4 py-2 bg-zinc-700 text-white hover:bg-zinc-600 rounded">Add</button>
            </div>
          </div>

          <button type="submit" disabled={loading || form.sizes.length === 0 || form.colors.length === 0} className="w-full py-4 bg-green-500 text-black font-bold hover:bg-green-400 disabled:opacity-50">
            {loading ? "Adding..." : "Add Product"}
          </button>
        </form>
      </div>
    </div>
  );
}

// Product Card
function ProductCard({ product }) {
  const navigate = useNavigate();
  const discountedPrice = (product.price * 0.9).toFixed(2);
  return (
    <div className="group bg-zinc-900/50 rounded-lg overflow-hidden border border-white/5 hover:border-amber-500/30 transition-all cursor-pointer relative" onClick={() => navigate(`/product/${product.id}`)}>
      <div className="absolute top-3 left-3 z-10 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
        10% OFF
      </div>
      <div className="aspect-square overflow-hidden bg-zinc-800">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-4">
        <span className="text-amber-400/80 text-xs tracking-wider uppercase">{product.category}</span>
        <h3 className="text-white font-medium mt-1 text-lg">{product.name}</h3>
        <div className="mt-2 flex items-center gap-2">
          <p className="text-amber-400 font-bold text-xl">${discountedPrice}</p>
          <p className="text-white/40 text-sm line-through">${product.price.toFixed(2)}</p>
        </div>
      </div>
    </div>
  );
}

// Product Grid
function ProductGrid({ products }) {
  if (!products || products.length === 0) return <p className="text-white/60 text-center">No products found</p>;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {products.map((p, i) => <ProductCard key={p.id || i} product={p} />)}
    </div>
  );
}

// Hero
function HeroSection() {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black" />
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto pt-16">
        <img src={BRAND_LOGO} alt="Fresh Po'Boy Streetwear LLC" className="h-48 sm:h-64 lg:h-72 w-auto mx-auto mb-8" />
        <h1 className="font-bebas text-5xl sm:text-7xl lg:text-8xl text-white tracking-tight leading-none mb-4">
          Street Style<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-cyan-400">Redefined</span>
        </h1>
        <p className="text-white/60 text-lg sm:text-xl max-w-xl mx-auto mb-8">Premium streetwear for those who dare to stand out. T-Shirts, Hoodies & Sweaties.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button onClick={() => navigate('/shop')} className="px-8 py-4 bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors">Shop Now</button>
          <button onClick={() => navigate('/sports')} className="px-8 py-4 border-2 border-cyan-400 text-cyan-400 font-semibold hover:bg-cyan-400 hover:text-black transition-colors">Sports Collection</button>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 py-4 bg-gradient-to-r from-red-600 via-red-500 to-red-600 overflow-hidden">
        <div className="animate-marquee whitespace-nowrap">
          <span className="text-white font-semibold mx-4">10% OFF EVERYTHING • Free Shipping Over $100 • New Drops Weekly • Exclusive Designs • 10% OFF EVERYTHING • Free Shipping Over $100 • New Drops Weekly •</span>
        </div>
      </div>
    </section>
  );
}

// Featured Section
function FeaturedSection({ products }) {
  const navigate = useNavigate();
  const featured = products.filter(p => p.collection === "Featured");
  return (
    <section className="py-20 px-4 bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div>
            <span className="text-red-500 text-sm tracking-widest uppercase">Featured</span>
            <h2 className="text-white text-4xl sm:text-5xl font-bebas mt-2">Hot Right Now</h2>
          </div>
          <button onClick={() => navigate('/shop')} className="flex items-center gap-2 text-white/60 hover:text-amber-400">View All <ChevronRight size={20} /></button>
        </div>
        <ProductGrid products={featured} />
      </div>
    </section>
  );
}

// Anime Section
function AnimeSection({ products, isPage }) {
  const navigate = useNavigate();
  const animeProducts = products.filter(p => p.collection === "Anime");
  
  const taglines = [
    "Anime in the Streets.",
    "Drip Like Your Favorite Character.",
    "Main Character Energy.",
    "Level Up Your Style."
  ];
  
  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-32' : ''} bg-gradient-to-b from-zinc-900 to-black`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-cyan-400 text-sm tracking-widest uppercase">New Collection</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">Anime <span className="text-cyan-400">Collection</span></h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Where streetwear meets anime culture.</p>
        </div>
        
        {/* Anime Taglines */}
        {isPage && (
          <div className="mb-16">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {taglines.map((tagline, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl ${index % 2 === 0 ? 'bg-cyan-400/10 border border-cyan-400/30' : 'bg-red-600/10 border border-red-600/30'} text-center`}
                >
                  <p className={`text-sm sm:text-base font-bold ${index % 2 === 0 ? 'text-cyan-400' : 'text-red-500'}`}>
                    "{tagline}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <ProductGrid products={animeProducts} />
        {!isPage && animeProducts.length > 0 && (
          <div className="text-center mt-12">
            <button onClick={() => navigate('/anime')} className="px-8 py-4 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 transition-colors">View Full Collection</button>
          </div>
        )}
      </div>
    </section>
  );
}

// Streetwear Section
function StreetwearSection({ products, isPage }) {
  const navigate = useNavigate();
  const streetwearProducts = products.filter(p => p.collection === "Streetwear");
  
  const taglines = [
    "Different by Design.",
    "Stand Out. Stay Real.",
    "Unapologetically Me.",
    "Born to Lead, Not Follow."
  ];
  
  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-32' : ''} bg-gradient-to-b from-black to-zinc-900`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-red-500 text-sm tracking-widest uppercase">Exclusive</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">Streetwear <span className="text-red-500">Collection</span></h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Bold designs for those who refuse to blend in.</p>
        </div>
        
        {/* Streetwear Taglines */}
        {isPage && (
          <div className="mb-16">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {taglines.map((tagline, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl ${index % 2 === 0 ? 'bg-red-600/10 border border-red-600/30' : 'bg-cyan-400/10 border border-cyan-400/30'} text-center`}
                >
                  <p className={`text-sm sm:text-base font-bold ${index % 2 === 0 ? 'text-red-500' : 'text-cyan-400'}`}>
                    "{tagline}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <ProductGrid products={streetwearProducts} />
        {!isPage && streetwearProducts.length > 0 && (
          <div className="text-center mt-12">
            <button onClick={() => navigate('/streetwear')} className="px-8 py-4 bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors">View Full Collection</button>
          </div>
        )}
      </div>
    </section>
  );
}

// Hoodies Section
function HoodiesSection({ products, isPage }) {
  const navigate = useNavigate();
  const hoodieProducts = products.filter(p => p.collection === "Hoodies");
  
  const taglines = [
    "Heavyweight. Heavy Drip.",
    "Comfort Meets Confidence.",
    "Stay Warm. Stay Fresh.",
    "Purpose in Every Thread."
  ];
  
  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-32' : ''} bg-gradient-to-b from-zinc-900 to-black`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-cyan-400 text-sm tracking-widest uppercase">Premium</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">Hoodies <span className="text-cyan-400">Collection</span></h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Stay warm. Stay fresh. Premium heavyweight hoodies.</p>
        </div>
        
        {/* Hoodies Taglines */}
        {isPage && (
          <div className="mb-16">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {taglines.map((tagline, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl ${index % 2 === 0 ? 'bg-cyan-400/10 border border-cyan-400/30' : 'bg-red-600/10 border border-red-600/30'} text-center`}
                >
                  <p className={`text-sm sm:text-base font-bold ${index % 2 === 0 ? 'text-cyan-400' : 'text-red-500'}`}>
                    "{tagline}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <ProductGrid products={hoodieProducts} />
        {!isPage && hoodieProducts.length > 0 && (
          <div className="text-center mt-12">
            <button onClick={() => navigate('/hoodies')} className="px-8 py-4 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 transition-colors">View All Hoodies</button>
          </div>
        )}
      </div>
    </section>
  );
}

// Hip Hop Section
function HipHopSection({ products, isPage }) {
  const navigate = useNavigate();
  const hiphopProducts = products.filter(p => p.collection === "Hip Hop");
  
  const taglines = [
    "Legends Never Die. Neither Do We.",
    "From the Block to the Top.",
    "Real Recognize Real.",
    "Culture Runs Deep."
  ];
  
  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-32' : ''} bg-gradient-to-b from-black via-red-950/20 to-black`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-red-500 text-sm tracking-widest uppercase">Urban Legends</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">Hip Hop <span className="text-red-500">Collection</span></h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Pay homage to the legends. Urban streetwear celebrating hip hop culture.</p>
        </div>
        
        {/* Hip Hop Taglines */}
        {isPage && (
          <div className="mb-16">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {taglines.map((tagline, index) => (
                <div 
                  key={index} 
                  className="p-4 rounded-xl bg-red-600/10 border border-red-600/30 text-center"
                >
                  <p className="text-sm sm:text-base font-bold text-red-500">
                    "{tagline}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <ProductGrid products={hiphopProducts} />
        {!isPage && hiphopProducts.length > 0 && (
          <div className="text-center mt-12">
            <button onClick={() => navigate('/hiphop')} className="px-8 py-4 bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors">View Full Collection</button>
          </div>
        )}
      </div>
    </section>
  );
}

// Faith Section
function FaithSection({ products, isPage }) {
  const navigate = useNavigate();
  const faithProducts = products.filter(p => p.collection === "Faith");
  
  const taglines = [
    "Covered by Grace. Built for the Grind.",
    "Walk by Faith. Move with Purpose.",
    "Kingdom Mindset. Street Certified.",
    "I Serve a God Who Never Fails — And Neither Do I."
  ];
  
  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-32' : ''} bg-gradient-to-b from-black via-cyan-950/10 to-black`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-cyan-400 text-sm tracking-widest uppercase">Faith-Driven</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">Faith <span className="text-cyan-400">Collection</span></h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Wear your faith. Inspire others. Premium faith-based streetwear.</p>
        </div>
        
        {/* Faith Taglines */}
        {isPage && (
          <div className="mb-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {taglines.map((tagline, index) => (
                <div 
                  key={index} 
                  className={`p-6 rounded-xl border ${index % 2 === 0 ? 'bg-red-600/10 border-red-600/30' : 'bg-cyan-400/10 border-cyan-400/30'} text-center`}
                >
                  <p className={`text-lg sm:text-xl font-bold ${index % 2 === 0 ? 'text-red-500' : 'text-cyan-400'}`}>
                    "{tagline}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <ProductGrid products={faithProducts} />
        {!isPage && faithProducts.length > 0 && (
          <div className="text-center mt-12">
            <button onClick={() => navigate('/faith')} className="px-8 py-4 bg-cyan-400 text-black font-semibold hover:bg-cyan-300 transition-colors">View Full Collection</button>
          </div>
        )}
      </div>
    </section>
  );
}

// Sports Section
function SportsSection({ products, isPage }) {
  const navigate = useNavigate();
  const sportsProducts = products.filter(p => p.collection === "Sports");
  
  const taglines = [
    "Mamba Mentality. Forever.",
    "Legends Play Different.",
    "Heart of a Champion.",
    "Built for Greatness."
  ];
  
  return (
    <section className={`py-20 px-4 ${isPage ? 'pt-32' : ''} bg-gradient-to-b from-black via-purple-950/10 to-black`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-purple-400 text-sm tracking-widest uppercase">Game Day</span>
          <h2 className="text-white text-5xl sm:text-6xl font-bebas mt-2">Sports <span className="text-purple-400">Collection</span></h2>
          <p className="text-white/60 mt-4 max-w-xl mx-auto">Rep your team. Own the game. Premium sports-inspired streetwear.</p>
        </div>
        
        {/* Sports Taglines */}
        {isPage && (
          <div className="mb-16">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {taglines.map((tagline, index) => (
                <div 
                  key={index} 
                  className={`p-4 rounded-xl ${index % 2 === 0 ? 'bg-purple-600/10 border border-purple-600/30' : 'bg-yellow-500/10 border border-yellow-500/30'} text-center`}
                >
                  <p className={`text-sm sm:text-base font-bold ${index % 2 === 0 ? 'text-purple-400' : 'text-yellow-400'}`}>
                    "{tagline}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <ProductGrid products={sportsProducts} />
        {!isPage && sportsProducts.length > 0 && (
          <div className="text-center mt-12">
            <button onClick={() => navigate('/sports')} className="px-8 py-4 bg-purple-600 text-white font-semibold hover:bg-purple-500 transition-colors">View Full Collection</button>
          </div>
        )}
      </div>
    </section>
  );
}

// Product Detail
function ProductDetail({ products, onAddToCart }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  
  const product = products.find(p => p.id === productId);

  useEffect(() => {
    if (product?.colors?.length > 0) setSelectedColor(product.colors[0]);
  }, [product]);

  if (!product) return <div className="min-h-screen bg-black flex items-center justify-center pt-16"><p className="text-white">Product not found</p></div>;

  const handleAddToCart = async () => {
    if (!selectedSize) { alert("Please select a size"); return; }
    setAdding(true);
    await onAddToCart(product.id, quantity, selectedSize, selectedColor);
    setAdding(false);
  };

  return (
    <div className="min-h-screen bg-black pt-24 pb-12 px-4">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white mb-8">← Back</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-4">
            <div className="aspect-square bg-zinc-900 rounded-lg overflow-hidden relative">
              <div className="absolute top-4 left-4 z-10 bg-red-500 text-white text-sm font-bold px-3 py-1.5 rounded">
                10% OFF
              </div>
              {showVideo && product.video ? (
                <video 
                  src={product.video} 
                  className="w-full h-full object-cover" 
                  controls 
                  autoPlay 
                  loop
                  playsInline
                />
              ) : (
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              )}
            </div>
            {product.video && (
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowVideo(false)} 
                  className={`flex-1 py-3 border rounded-lg font-semibold transition-all ${!showVideo ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white hover:border-white/40'}`}
                >
                  Image
                </button>
                <button 
                  onClick={() => setShowVideo(true)} 
                  className={`flex-1 py-3 border rounded-lg font-semibold transition-all ${showVideo ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white hover:border-white/40'}`}
                >
                  Video
                </button>
              </div>
            )}
          </div>
          <div className="space-y-6">
            <div>
              <span className="text-amber-400 text-sm tracking-widest uppercase">{product.collection} Collection</span>
              <h1 className="text-white text-4xl font-bebas mt-2">{product.name}</h1>
              <div className="mt-4 flex items-center gap-4">
                <p className="text-amber-400 text-3xl font-bold">${(product.price * 0.9).toFixed(2)}</p>
                <p className="text-white/40 text-xl line-through">${product.price.toFixed(2)}</p>
                <span className="bg-red-500/20 text-red-400 text-sm font-semibold px-3 py-1 rounded">SAVE ${(product.price * 0.1).toFixed(2)}</span>
              </div>
            </div>
            <p className="text-white/60">{product.description}</p>
            <div>
              <label className="text-white text-sm font-semibold block mb-3">Size</label>
              <div className="flex flex-wrap gap-3">
                {(product.sizes || []).map((s, i) => (
                  <button key={i} onClick={() => setSelectedSize(s)} className={`px-6 py-3 border ${selectedSize === s ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>{s}</button>
                ))}
              </div>
            </div>
            {product.colors && product.colors.length > 1 && (
              <div>
                <label className="text-white text-sm font-semibold block mb-3">Color</label>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((c, i) => (
                    <button key={i} onClick={() => setSelectedColor(c)} className={`px-6 py-3 border ${selectedColor === c ? 'border-amber-400 bg-amber-400 text-black' : 'border-white/20 text-white'}`}>{c}</button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-white text-sm font-semibold block mb-3">Quantity</label>
              <div className="flex items-center gap-4">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 border border-white/20 text-white"><Minus size={20} /></button>
                <span className="text-white text-xl font-semibold w-12 text-center">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-2 border border-white/20 text-white"><Plus size={20} /></button>
              </div>
            </div>
            <button onClick={handleAddToCart} disabled={adding} className="w-full py-4 bg-amber-500 text-black font-bold text-lg hover:bg-amber-400 disabled:opacity-50">
              {adding ? "Adding..." : "Add to Cart"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Cart Page
function CartPage({ cart, onRemove, onClear }) {
  const navigate = useNavigate();
  const total = cart.reduce((sum, item) => sum + (item.product?.price || 0) * item.quantity, 0);

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-white text-4xl font-bebas mb-8">Your Cart</h1>
          <div className="text-center py-16">
            <p className="text-white/60 text-lg mb-6">Your cart is empty</p>
            <button onClick={() => navigate('/shop')} className="px-8 py-4 bg-amber-500 text-black font-semibold">Continue Shopping</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-white text-4xl font-bebas mb-8">Your Cart</h1>
        <div className="space-y-4 mb-8">
          {cart.map((item, i) => (
            <div key={i} className="flex gap-4 bg-zinc-900/50 p-4 rounded-lg border border-white/5">
              <img src={item.product?.image} alt={item.product?.name} className="w-24 h-24 object-cover rounded" />
              <div className="flex-1">
                <h3 className="text-white font-medium">{item.product?.name}</h3>
                <p className="text-white/60 text-sm">Size: {item.size} {item.color && `| Color: ${item.color}`} | Qty: {item.quantity}</p>
                <p className="text-amber-400 font-bold mt-2">${((item.product?.price || 0) * item.quantity).toFixed(2)}</p>
              </div>
              <button onClick={() => onRemove(item.id)} className="text-white/40 hover:text-red-400"><Trash2 size={20} /></button>
            </div>
          ))}
        </div>
        <div className="border-t border-white/10 pt-6">
          <div className="flex justify-between items-center mb-6">
            <span className="text-white text-xl">Total</span>
            <span className="text-amber-400 text-3xl font-bold">${total.toFixed(2)}</span>
          </div>
          <div className="flex gap-4">
            <button onClick={onClear} className="px-6 py-3 border border-white/20 text-white hover:border-white/40">Clear Cart</button>
            <button className="flex-1 py-3 bg-amber-500 text-black font-bold hover:bg-amber-400">Checkout</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shop Page
function ShopPage({ products }) {
  const [filter, setFilter] = useState("all");
  const filteredProducts = filter === "all" ? products : products.filter(p => p.category.toLowerCase() === filter);

  return (
    <div className="min-h-screen bg-black pt-28 pb-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-white text-5xl sm:text-6xl font-bebas">Shop All</h1>
        </div>
        
        {/* Brand Intro */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-white/70 text-lg leading-relaxed">
            Welcome to <span className="text-cyan-400 font-semibold">Fresh Po'Boy Wear</span> — where bold faith meets real culture. 
            Every piece is crafted with premium heavy cotton and designed to make a statement without saying a word. 
            <span className="text-red-500 font-semibold"> This isn't just clothing. It's confidence you can wear.</span>
          </p>
        </div>
        
        <div className="flex justify-center gap-4 mb-12">
          <button onClick={() => setFilter("all")} className={`px-6 py-2 ${filter === "all" ? 'bg-red-600 text-white' : 'bg-zinc-800 text-white hover:bg-zinc-700'} transition-colors`}>All</button>
          <button onClick={() => setFilter("t-shirts")} className={`px-6 py-2 ${filter === "t-shirts" ? 'bg-red-600 text-white' : 'bg-zinc-800 text-white hover:bg-zinc-700'} transition-colors`}>T-Shirts</button>
          <button onClick={() => setFilter("hoodies")} className={`px-6 py-2 ${filter === "hoodies" ? 'bg-red-600 text-white' : 'bg-zinc-800 text-white hover:bg-zinc-700'} transition-colors`}>Hoodies</button>
        </div>
        <ProductGrid products={filteredProducts} />
      </div>
    </div>
  );
}

// Home Page
function HomePage({ products }) {
  const navigate = useNavigate();
  
  const categories = [
    {
      title: "Faith Over Everything",
      description: "My faith ain't quiet — it's loud, bold, and unapologetic.",
      link: "/faith",
      color: "cyan",
      icon: "✝"
    },
    {
      title: "Culture Classics",
      description: "We don't forget where we came from. Legends live forever.",
      link: "/hiphop",
      color: "red",
      icon: "🎤"
    },
    {
      title: "Everyday Confidence",
      description: "Every fit hits different when you know who you are.",
      link: "/streetwear",
      color: "cyan",
      icon: "🔥"
    },
    {
      title: "Limited Drops",
      description: "If you know, you know. Once it's gone, it's gone.",
      link: "/sports",
      color: "red",
      icon: "⚡"
    }
  ];
  
  return (
    <>
      <HeroSection />
      <FeaturedSection products={products} />
      
      {/* Category Vibes Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black via-zinc-900/50 to-black">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-red-500 text-sm tracking-widest uppercase">Shop By Vibe</span>
            <h2 className="text-white text-4xl sm:text-6xl font-bebas mt-2">Find Your <span className="text-cyan-400">Style</span></h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, index) => (
              <div 
                key={index}
                onClick={() => navigate(cat.link)}
                className={`group cursor-pointer p-8 rounded-2xl border transition-all duration-300 hover:-translate-y-2 ${
                  cat.color === 'cyan' 
                    ? 'bg-cyan-400/5 border-cyan-400/20 hover:border-cyan-400/50 hover:bg-cyan-400/10' 
                    : 'bg-red-600/5 border-red-600/20 hover:border-red-600/50 hover:bg-red-600/10'
                }`}
              >
                <div className="text-4xl mb-4">{cat.icon}</div>
                <h3 className={`font-bebas text-2xl mb-3 ${cat.color === 'cyan' ? 'text-cyan-400' : 'text-red-500'}`}>
                  {cat.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed mb-4">
                  {cat.description}
                </p>
                <span className={`text-sm font-semibold ${cat.color === 'cyan' ? 'text-cyan-400' : 'text-red-500'} group-hover:underline`}>
                  Shop Now →
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <AnimeSection products={products} isPage={false} />
      
      <section className="py-20 px-4 bg-black">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-red-500 text-sm tracking-widest uppercase">About Us</span>
            <h2 className="text-white text-4xl sm:text-5xl font-bebas mt-2">Born From The Streets</h2>
            <p className="text-white/60 mt-6 leading-relaxed">Fresh Po'Boy Streetwear LLC isn't just a brand—it's a movement. We create premium streetwear that speaks to the culture. T-Shirts, Hoodies & Sweaties.</p>
            <button onClick={() => navigate('/our-story')} className="mt-6 px-6 py-3 border-2 border-cyan-400 text-cyan-400 font-semibold hover:bg-cyan-400 hover:text-black transition-colors">
              Read Our Story
            </button>
          </div>
          <div className="aspect-square rounded-lg overflow-hidden border-2 border-red-600/30">
            <img src={BRAND_LOGO} alt="Fresh Po'Boy Streetwear" className="w-full h-full object-contain bg-black p-8" />
          </div>
        </div>
      </section>
      
      <footer className="py-8 px-4 bg-zinc-900 border-t border-red-600/30">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src={BRAND_LOGO} alt="Fresh Po'Boy" className="h-12 w-auto" />
          <a href="https://app.emergent.sh/?utm_source=emergent-badge" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/40 hover:text-white/60">
            <img src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4" alt="Emergent" className="w-5 h-5 rounded" />
            Made with Emergent
          </a>
        </div>
      </footer>
    </>
  );
}

// Lifestyle Gallery Page
function LifestylePage() {
  const lifestyleCategories = [
    {
      title: "City Streets",
      icon: "🏙️",
      color: "red",
      scenes: [
        {
          name: "Couple Goals",
          description: "Matching Fresh Po'Boy fits in the park. Grace skeleton hands design — bold faith, bold style.",
          vibe: "When you both walk in purpose.",
          image: "https://customer-assets.emergentagent.com/job_1818d098-2133-4078-9531-acababa9e2e1/artifacts/4u11xpys_front-and-back-mockup-of-a-couple-pointing-at-their-t-shirts-29784.png"
        },
        {
          name: "Corner Store Confidence",
          description: "Group of friends posted up outside a bodega, laughing and talking. Mixed fits — hoodies, tees, layered looks.",
          vibe: "Real neighborhood energy.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/c46f0e16c79532f87168968f744776dc1cdcb1c2c6ff42ebd2c31f50f14a5d36.png"
        },
        {
          name: "The Commute",
          description: "Stepping off a subway or bus, headphones on, rocking a Hip Hop Legends tee. Urban transit backdrop.",
          vibe: "Focused. Purpose-driven.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/07a33af545f30caf04489fd4454ceee326121a7115057c221d90d13b944cfb09.png"
        }
      ]
    },
    {
      title: "Sports Collection",
      icon: "🏀",
      color: "cyan",
      scenes: [
        {
          name: "Mamba Forever - Purple Long Sleeve",
          description: "Kobe Bean Bryant tribute long sleeve. Front Black Mamba logo with sleeve prints. Lakers purple.",
          vibe: "Hall of Fame Class of 2020.",
          image: "https://customer-assets.emergentagent.com/job_1818d098-2133-4078-9531-acababa9e2e1/artifacts/vdcn5fvj_black%20mamba%20purple%20long%20sl%20tee.png"
        },
        {
          name: "Mamba Forever - Back Design",
          description: "Full back graphic featuring iconic Kobe moments. Black Mamba snake and gold accents.",
          vibe: "Legends never die.",
          image: "https://customer-assets.emergentagent.com/job_1818d098-2133-4078-9531-acababa9e2e1/artifacts/nekj1kjr_black%20mamba%20longsleeve%20t%20back.png"
        },
        {
          name: "Legendary Gold Hoodie",
          description: "Premium heavyweight hoodie in Lakers gold. Front Mamba logo, back Legendary 1978-2020 tribute.",
          vibe: "Honor the GOAT.",
          image: "https://customer-assets.emergentagent.com/job_1818d098-2133-4078-9531-acababa9e2e1/artifacts/6m8w19j5_black%20mamba%20yellow.png"
        }
      ]
    },
    {
      title: "Legends Collection",
      icon: "👑",
      color: "red",
      scenes: [
        {
          name: "Legends White Tee",
          description: "Front Black Mamba logo, back Kobe Hall of Fame tribute. Clean white colorway.",
          vibe: "Kings recognize kings.",
          image: "https://customer-assets.emergentagent.com/job_1818d098-2133-4078-9531-acababa9e2e1/artifacts/ngdnjylu_black%20mamba%20white%20tshirt.png"
        },
        {
          name: "Studio Session",
          description: "Artist in the booth or behind the boards, wearing a Culture Classics hoodie. Red lighting, mic in frame.",
          vibe: "Creative energy.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/1baa26ee942d3edabb00bace597421c6d2d2fc533dd81f8e5f7975c3b918a228.png"
        },
        {
          name: "Open Mic Night",
          description: "Spoken word or rap performance at a small venue. Performer on stage in a Faith Collection piece.",
          vibe: "Raw. Authentic.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/faffc5508cfa5dc01866b66db74b8b43a13a8ef06cf307ce34a89d3d9ff07ec1.png"
        }
      ]
    },
    {
      title: "Chill Hangouts",
      icon: "🛋️",
      color: "cyan",
      scenes: [
        {
          name: "Rooftop Vibes",
          description: "Friends gathered on a rooftop at golden hour, city view behind them. Mixed Fresh Po'Boy fits.",
          vibe: "Relaxed. Real conversation.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/93cfa9e780c8aeb6fb513cb7b9b8f0af7cb35b0afab8b0550ce24736f641aea1.png"
        },
        {
          name: "Barbershop Talk",
          description: "Inside a barbershop — someone in the chair wearing a Streetwear tee, barber working.",
          vibe: "Community gathering spot.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/b7c509c26aff9c2448c94e342435738ce15dea425689bdf5819b60ddea821ac3.png"
        },
        {
          name: "Game Night",
          description: "Living room scene — friends playing video games or cards, wearing hoodies and tees.",
          vibe: "Genuine laughter.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/3c239d591cbb3362f7fe2934d846c4e83b1ecf5d7ed97f301c6b1477ee111814.png"
        }
      ]
    },
    {
      title: "Faith Moments",
      icon: "🙏",
      color: "red",
      scenes: [
        {
          name: "Sunday Best, Street Edition",
          description: "Walking into or out of church, dressed in Faith Collection gear. Bible or journal in hand.",
          vibe: "Modern faith. Urban expression.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/2b0c46d1eede011a5f41a89f225a4bb25a6b0bd8f11bf39d30fd0498cb092c7d.png"
        },
        {
          name: "Prayer Before the Play",
          description: "Basketball court — player kneeling or head bowed before a game, wearing Sports Collection.",
          vibe: "Quiet moment of faith.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/eca9292a451c701b2813080bffdd5ba76f436cadbbaca867884a2e928a8c0fa1.png"
        },
        {
          name: "Bible & Coffee",
          description: "Morning devotional scene — someone at a café table, Bible open, wearing a 'Covered by Grace' tee.",
          vibe: "Peaceful. Intentional.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/d262ac737bcfdaa74fed2f7f97cce38eac72ff381e634ff1636e7acfe9c68886.png"
        }
      ]
    },
    {
      title: "Solo Portraits",
      icon: "📸",
      color: "cyan",
      scenes: [
        {
          name: "Against the Wall",
          description: "Clean shot against a graffiti wall or brick backdrop. Arms crossed, confident stance.",
          vibe: "Full outfit showcase.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/5db1e013a7bce5ff0bb8da445c9951c861deed52abfe2618d278f5e63237df06.png"
        },
        {
          name: "Stairwell Drip",
          description: "Urban stairwell or fire escape. Sitting on steps, looking up at camera. Moody lighting.",
          vibe: "Street aesthetic.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/e311dbe2de95de90ed4e5950611ddafd448afd828824d4897d7c3e5a634ae755.png"
        },
        {
          name: "Mirror Check",
          description: "Reflection shot in a gym mirror or storefront window. Adjusting fit, self-assured expression.",
          vibe: "Confidence on display.",
          image: "https://static.prod-images.emergentagent.com/jobs/1818d098-2133-4078-9531-acababa9e2e1/images/6a0c289d21e7a8f3580d791b63234a17f3aa1dd6126ef7b60601a3bc98ac42f2.png"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black pt-24 pb-20">
      {/* Hero Section */}
      <section className="relative py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-400/10 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <span className="text-red-500 text-sm tracking-widest uppercase">The Culture</span>
          <h1 className="font-bebas text-5xl sm:text-7xl text-white tracking-tight mt-2 mb-4">Lifestyle Gallery</h1>
          <p className="text-white/60 text-lg sm:text-xl max-w-2xl mx-auto">
            Real people. Real moments. Fresh Po'Boy isn't just worn — it's lived in. 
            Here's how our community rocks the culture.
          </p>
        </div>
      </section>

      {/* Lifestyle Categories */}
      {lifestyleCategories.map((category, catIndex) => (
        <section key={catIndex} className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <span className="text-4xl">{category.icon}</span>
              <h2 className={`font-bebas text-3xl sm:text-4xl ${category.color === 'cyan' ? 'text-cyan-400' : 'text-red-500'}`}>
                {category.title}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {category.scenes.map((scene, sceneIndex) => (
                <div 
                  key={sceneIndex}
                  className={`group relative rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 ${
                    category.color === 'cyan' 
                      ? 'bg-gradient-to-br from-cyan-900/20 to-black border-cyan-400/20 hover:border-cyan-400/50' 
                      : 'bg-gradient-to-br from-red-900/20 to-black border-red-600/20 hover:border-red-600/50'
                  }`}
                >
                  {/* Image Area */}
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={scene.image} 
                      alt={scene.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`font-bebas text-xl mb-2 ${category.color === 'cyan' ? 'text-cyan-400' : 'text-red-500'}`}>
                      {scene.name}
                    </h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-3">
                      {scene.description}
                    </p>
                    <p className={`text-sm font-semibold italic ${category.color === 'cyan' ? 'text-cyan-400/80' : 'text-red-500/80'}`}>
                      "{scene.vibe}"
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-red-600/20 via-red-600/10 to-red-600/20 rounded-2xl p-12 border border-red-600/30">
            <h3 className="font-bebas text-3xl sm:text-4xl text-white mb-4">Be Part of the Culture</h3>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">
              Tag us @freshpoboywear in your photos and you might be featured in our gallery. Show us how you rock the drip.
            </p>
            <a href="/shop" className="inline-block px-8 py-4 bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors">
              Shop the Collection
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// Our Story Page
function OurStoryPage() {
  return (
    <div className="min-h-screen bg-black pt-24 pb-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-red-600/10 via-transparent to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <img src={BRAND_LOGO} alt="Fresh Po'Boy" className="h-32 sm:h-40 w-auto mx-auto mb-8" />
          <h1 className="font-bebas text-5xl sm:text-7xl text-white tracking-tight mb-4">Our Story</h1>
          <p className="text-white/60 text-lg sm:text-xl max-w-2xl mx-auto">
            More than streetwear. A movement built on quality, culture, and community.
          </p>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-zinc-900/80 to-black rounded-2xl p-8 sm:p-12 border border-cyan-400/20">
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              <span className="text-cyan-400 font-semibold">Fresh Po'Boy Wear</span> was built on more than fabric and ink — it was built on <span className="text-red-500 font-semibold">faith, resilience, and the belief that everyone has a story worth wearing.</span> What started as a simple idea grew into a movement: apparel that speaks boldly, lives loudly, and carries meaning with every stitch.
            </p>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              We create pieces for the ones who stand tall in who they are. The ones who hustle with purpose, love with intention, and walk through life with a little extra edge. Every design reflects the culture that raised us, the lessons that shaped us, and the hope that keeps us moving forward.
            </p>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              Our garments are crafted with <span className="text-cyan-400 font-semibold">premium heavy cotton</span> because quality matters. Your story deserves a canvas that lasts — something solid, dependable, and built to go wherever life takes you. From faith-inspired messages to streetwise graphics, every drop is designed to spark confidence and connection.
            </p>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              Fresh Po'Boy Wear isn't just clothing. It's <span className="text-red-500 font-semibold">identity</span>. It's <span className="text-red-500 font-semibold">community</span>. It's a reminder that you can rise from anything and still shine. When you wear our pieces, you're not just putting on a shirt — you're stepping into your purpose with style, strength, and unapologetic authenticity.
            </p>
            <div className="text-center mt-10 pt-8 border-t border-white/10">
              <p className="text-white/60 text-lg italic mb-4">This is who we are.</p>
              <p className="text-cyan-400 text-xl font-semibold mb-6">This is Fresh Po'Boy Wear.</p>
              <div className="flex justify-center gap-4 flex-wrap">
                <span className="px-4 py-2 bg-red-600/20 text-red-500 font-bold rounded-full text-sm tracking-wider">BOLD</span>
                <span className="px-4 py-2 bg-cyan-400/20 text-cyan-400 font-bold rounded-full text-sm tracking-wider">ROOTED</span>
                <span className="px-4 py-2 bg-red-600/20 text-red-500 font-bold rounded-full text-sm tracking-wider">UNSTOPPABLE</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Material Story Section */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-cyan-400 text-sm tracking-widest uppercase">Our Materials</span>
            <h2 className="font-bebas text-4xl sm:text-6xl text-white mt-2">Why We Use Heavy Cotton</h2>
          </div>
          
          <div className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 rounded-2xl p-8 sm:p-12 border border-red-600/20 mb-12">
            <p className="text-white/80 text-lg sm:text-xl leading-relaxed text-center">
              At <span className="text-cyan-400 font-semibold">Fresh Po'Boy Wear</span>, every piece starts with intention. 
              We choose heavy cotton because it delivers the quality, comfort, and durability our community deserves. 
              This isn't fast fashion — <span className="text-red-500 font-semibold">it's gear built to last.</span>
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Card 1 */}
            <div className="bg-zinc-900/50 rounded-xl p-8 border border-white/5 hover:border-cyan-400/30 transition-all group">
              <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600/30 transition-colors">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-bebas text-2xl text-white mb-3">Built for Everyday Wear</h3>
              <p className="text-white/60 leading-relaxed">
                Heavy cotton holds its shape, keeps its structure, and feels solid from the moment you put it on. 
                No flimsy fabric. No see-through tees. Just a <span className="text-cyan-400">premium feel you can trust.</span>
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-zinc-900/50 rounded-xl p-8 border border-white/5 hover:border-cyan-400/30 transition-all group">
              <div className="w-16 h-16 bg-cyan-400/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-400/30 transition-colors">
                <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <h3 className="font-bebas text-2xl text-white mb-3">A Better Canvas for Bold Designs</h3>
              <p className="text-white/60 leading-relaxed">
                Our graphics carry meaning — culture, confidence, faith, and attitude. Heavy cotton gives those designs 
                a <span className="text-red-500">richer look and longer life</span>, so your shirt stays sharp wash after wash.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-zinc-900/50 rounded-xl p-8 border border-white/5 hover:border-cyan-400/30 transition-all group">
              <div className="w-16 h-16 bg-red-600/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-red-600/30 transition-colors">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h3 className="font-bebas text-2xl text-white mb-3">Comfort That Gets Better Over Time</h3>
              <p className="text-white/60 leading-relaxed">
                The more you wear it, the better it feels. Heavy cotton softens naturally while staying strong, 
                giving you that <span className="text-cyan-400">perfect balance of comfort and durability.</span>
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-zinc-900/50 rounded-xl p-8 border border-white/5 hover:border-cyan-400/30 transition-all group">
              <div className="w-16 h-16 bg-cyan-400/20 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-400/30 transition-colors">
                <svg className="w-8 h-8 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="font-bebas text-2xl text-white mb-3">Quality You Can Feel</h3>
              <p className="text-white/60 leading-relaxed">
                Pick it up and you'll notice the difference immediately. The weight, the texture, the way it drapes — 
                it all speaks to <span className="text-red-500">craftsmanship and care.</span>
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-red-600/20 via-red-600/10 to-red-600/20 rounded-2xl p-12 border border-red-600/30">
            <h3 className="font-bebas text-3xl sm:text-4xl text-white mb-4">Experience the Difference</h3>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">
              Ready to feel what premium streetwear is all about? Explore our collections and discover gear that's built to last.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/shop" className="px-8 py-4 bg-red-600 text-white font-semibold hover:bg-red-500 transition-colors">
                Shop All Products
              </a>
              <a href="/sports" className="px-8 py-4 border-2 border-cyan-400 text-cyan-400 font-semibold hover:bg-cyan-400 hover:text-black transition-colors">
                View Sports Collection
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// Main App
function App() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  const fetchProducts = async () => {
    try { const res = await axios.get(`${API}/products`); setProducts(res.data); } catch (e) { console.error(e); }
  };

  const fetchCart = async () => {
    try { const res = await axios.get(`${API}/cart`); setCart(res.data); } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchProducts(); fetchCart(); }, []);

  const addToCart = async (productId, quantity, size, color) => {
    try { await axios.post(`${API}/cart`, { product_id: productId, quantity, size, color }); fetchCart(); } catch (e) { console.error(e); }
  };

  const removeFromCart = async (itemId) => {
    try { await axios.delete(`${API}/cart/${itemId}`); fetchCart(); } catch (e) { console.error(e); }
  };

  const clearCart = async () => {
    try { await axios.delete(`${API}/cart`); setCart([]); } catch (e) { console.error(e); }
  };

  return (
    <AuthProvider>
      <div className="App bg-black min-h-screen">
        <BrowserRouter>
          <Navbar cartCount={cart.length} onMenuClick={() => setMenuOpen(!menuOpen)} menuOpen={menuOpen} />
          <Routes>
            <Route path="/" element={<HomePage products={products} />} />
            <Route path="/shop" element={<ShopPage products={products} />} />
            <Route path="/our-story" element={<OurStoryPage />} />
            <Route path="/lifestyle" element={<LifestylePage />} />
            <Route path="/hiphop" element={<HipHopSection products={products} isPage={true} />} />
            <Route path="/faith" element={<FaithSection products={products} isPage={true} />} />
            <Route path="/sports" element={<SportsSection products={products} isPage={true} />} />
            <Route path="/streetwear" element={<StreetwearSection products={products} isPage={true} />} />
            <Route path="/hoodies" element={<HoodiesSection products={products} isPage={true} />} />
            <Route path="/anime" element={<AnimeSection products={products} isPage={true} />} />
            <Route path="/product/:productId" element={<ProductDetail products={products} onAddToCart={addToCart} />} />
            <Route path="/cart" element={<CartPage cart={cart} onRemove={removeFromCart} onClear={clearCart} />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/add-product" element={<AddProductPage onProductAdded={fetchProducts} />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
